
'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { SocialStory, HighlightStoryGroup } from '@/lib/types';
import { getStoriesByIds } from '@/lib/actions';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { X, ChevronLeft, ChevronRight, VolumeX, Volume2, Link as LinkIcon, Play, Pause } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Slider } from '../ui/slider';
import Image from '@/components/ui/Image';
import ReactMarkdown from 'react-markdown';
import { Loader2 } from 'lucide-react';

const STORY_IMAGE_DURATION = 5000; // 5 seconds for images

interface HighlightStoryViewerProps {
  group: HighlightStoryGroup;
  onClose: () => void;
}

export function HighlightStoryViewer({ group, onClose }: HighlightStoryViewerProps) {
  const [stories, setStories] = useState<SocialStory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const timerRef = useRef<any>(null);
  const startTimeRef = useRef<number>(Date.now());
  const pausedTimeRef = useRef<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [volume, setVolume] = useState(0);
  
  useEffect(() => {
    async function fetchStories() {
      setIsLoading(true);
      const { stories: fetchedStories, error } = await getStoriesByIds(group.storyIds);
      if (error) {
        console.error("Failed to fetch highlight stories:", error);
        onClose();
      } else {
        setStories(fetchedStories);
      }
      setIsLoading(false);
    }
    fetchStories();
  }, [group.storyIds, onClose]);
  
  const activeStory = stories[currentStoryIndex];

  const advance = useCallback(() => {
    if (currentStoryIndex < stories.length - 1) {
      setCurrentStoryIndex(prev => prev + 1);
    } else {
      onClose();
    }
  }, [currentStoryIndex, stories.length, onClose]);

  const goToNextStory = useCallback(() => {
    if(timerRef.current) cancelAnimationFrame(timerRef.current);
    advance();
  }, [advance]);

  const goToPrevStory = useCallback(() => {
    if(timerRef.current) cancelAnimationFrame(timerRef.current);
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(prev => prev - 1);
    }
  }, [currentStoryIndex]);

  useEffect(() => {
    if (!activeStory || isLoading) return;

    setProgress(0);
    startTimeRef.current = Date.now();
    pausedTimeRef.current = null;
    if (timerRef.current) cancelAnimationFrame(timerRef.current);
    
    if (activeStory.mediaType === 'image') {
      const runTimer = () => {
        if (pausedTimeRef.current) return;
        const elapsed = Date.now() - startTimeRef.current;
        const newProgress = (elapsed / STORY_IMAGE_DURATION) * 100;
        if (newProgress >= 100) {
          advance();
        } else {
          setProgress(newProgress);
          timerRef.current = requestAnimationFrame(runTimer);
        }
      };
      timerRef.current = requestAnimationFrame(runTimer);
    } else if (activeStory.mediaType === 'video' && videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play().catch(e => console.error("Video play failed", e));
    }
    return () => { if (timerRef.current) cancelAnimationFrame(timerRef.current) };
  }, [activeStory, isLoading, advance]);

  const handlePause = () => {
    if(pausedTimeRef.current === null) {
        if(timerRef.current) cancelAnimationFrame(timerRef.current);
        pausedTimeRef.current = Date.now();
        if(videoRef.current) videoRef.current.pause();
    }
  }

  const handleResume = () => {
    if (pausedTimeRef.current && startTimeRef.current) {
      const pauseDuration = Date.now() - pausedTimeRef.current;
      startTimeRef.current += pauseDuration;
      pausedTimeRef.current = null;
      if (activeStory?.mediaType === 'image') {
         timerRef.current = requestAnimationFrame(() => {
            const runTimer = () => {
              if (pausedTimeRef.current) return;
              const elapsed = Date.now() - startTimeRef.current;
              const newProgress = (elapsed / STORY_IMAGE_DURATION) * 100;
              if (newProgress >= 100) advance();
              else {
                setProgress(newProgress);
                timerRef.current = requestAnimationFrame(runTimer);
              }
            };
            runTimer();
        });
      } else if (videoRef.current) {
        videoRef.current.play().catch(e => console.error("Video resume failed", e));
      }
    }
  }

  const handleVideoTimeUpdate = () => {
      if (videoRef.current && videoRef.current.duration) {
          const newProgress = (videoRef.current.currentTime / videoRef.current.duration) * 100;
          setProgress(newProgress);
      }
  }

  const toggleMute = (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsMuted(!isMuted);
  };
  
  const getStoryTimestamp = (dateInput: any) => {
    try {
      let date: Date;
      if (dateInput && typeof dateInput.toDate === 'function') {
        date = dateInput.toDate();
      } else {
        date = new Date(dateInput);
      }
      if (isNaN(date.getTime())) return '';
      return format(date, "d MMM yyyy", { locale: es });
    } catch (e) {
        return '';
    }
  }

  if (isLoading) {
    return (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
            <Loader2 className="h-10 w-10 text-white animate-spin" />
        </div>
    )
  }

  if (!activeStory) {
    return null;
  }
  
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center animate-in fade-in-0" onContextMenu={(e) => e.preventDefault()}>
      <div className="relative w-full max-w-[400px] aspect-[9/16] bg-black rounded-lg overflow-hidden shadow-2xl">
        <div className="absolute top-0 left-0 right-0 p-4 z-10 bg-gradient-to-b from-black/50 to-transparent">
          <div className="flex items-center gap-2">
            {stories.map((_, index) => (
              <div key={index} className="flex-1 h-0.5 bg-white/30 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white rounded-full transition-all duration-100 ease-linear" 
                  style={{ width: `${index < currentStoryIndex ? 100 : (index === currentStoryIndex ? progress : 0)}%` }}
                />
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-3">
             <div className="flex items-center gap-3">
                <Avatar className="h-9 w-9 border-2 border-white/80">
                    <AvatarImage src={group.coverUrl} />
                    <AvatarFallback>{group.name.substring(0,2)}</AvatarFallback>
                </Avatar>
                <div className='flex items-center gap-2'>
                    <span className="text-white font-semibold text-sm">{group.name}</span>
                    <span className="text-white/70 text-xs">{getStoryTimestamp(activeStory.createdAt)}</span>
                </div>
             </div>
             <div className='flex items-center gap-2'>
                {activeStory.mediaType === 'video' && (
                    <button onClick={toggleMute} className="text-white/80 hover:text-white transition-colors">
                        {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                    </button>
                )}
                <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
                    <X size={24} />
                </button>
             </div>
          </div>
        </div>
        
        {activeStory.mediaType === 'video' ? (
            <video
                ref={videoRef}
                src={activeStory.mediaUrl as string}
                className="w-full h-full object-contain"
                autoPlay
                playsInline
                muted={isMuted}
                onEnded={() => advance()}
                onTimeUpdate={handleVideoTimeUpdate}
                key={activeStory.id}
            />
        ) : (
             <Image 
                src={activeStory.mediaUrl as string}
                alt={`Historia de ${group.name}`} 
                className="object-contain w-full h-full"
            />
        )}
        
        <div className="absolute inset-0 flex">
            <div className="flex-1" onClick={goToPrevStory} onMouseDown={handlePause} onMouseUp={handleResume} onMouseLeave={handleResume} />
            <div className="flex-1" onClick={goToNextStory} onMouseDown={handlePause} onMouseUp={handleResume} onMouseLeave={handleResume} />
        </div>
        
         <button onClick={goToPrevStory} className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-black rounded-full h-8 w-8 flex items-center justify-center">
            <ChevronLeft size={20} />
         </button>
         <button onClick={goToNextStory} className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-black rounded-full h-8 w-8 flex items-center justify-center">
            <ChevronRight size={20} />
         </button>
      </div>
    </div>
  );
}
